/**
 * LocationMap
 * 
 * @Author: Focci
 * @Date: 2023-08-08 15:01:41
 * @Last Modified by: Focci
 * @Last Modified time: 2023-08-08 15:01:41
 */

'use client'

import PropTypes from 'prop-types'
import Mapbox, { Layer, Marker, Popup, useMapState } from '@comp/mapbox'
import useApp from '@hook/useApp'
import OverallLayer from '@comp/mapbox/controller/overall/OverallLayer'
import LayersController from '@comp/mapbox/controller/LayersController'
import { map } from 'lodash-es'
import { useSchoolOptions } from '@comp/mapbox/controller/overall/useMapboxOverall'
import { Marker as Locale } from 'react-map-gl'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { setDefaultMarker } from '@comp/page/search/mix/lib'
import FullscreenController from '@comp/mapbox/controller/FullscreenController'
import NavigationController from '@comp/mapbox/controller/NavigationController'
import UnitaryHoverPopup from '@comp/mapbox/controller/UnitaryHoverPopup'
import useTracking from '@hook/useTracking'
import SVGLocation from '@img/locale-verti.svg'
import { houseToGta, isAU } from '@lib/utils'
import LocationBoundary from './LocationBoundary'

function MapboxInitial({ type, defaultTabName, locationBarOptionValue }) {
  const mps = useMapState()
  const schoolOpts = useSchoolOptions()
  
  // https://7thave.atlassian.net/browse/HT-1968
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { defaultTabName !== 'development' && setDefaultMarker(type, mps) }, [])

  useEffect(() => {
    // 显示学校
    if(defaultTabName === 'schoolPanel') {
      mps.valueSetter('school', true)
      mps.valueSetter('schoolParam', { ...mps.schoolParam, type: map(schoolOpts, 'value') })
    }

    // 显示开发
    if(defaultTabName === 'development' && locationBarOptionValue) {
      mps.setLayers((prev) => ({ ...prev, display: [locationBarOptionValue] }))
    }
  
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultTabName, locationBarOptionValue])
}

function UnitaryPopup() {
  const mps = useMapState()
  if (mps.unitaryPlanSuburban
    || mps.unitaryPlanUrban
    || mps.unitaryPlanApartment
    || mps.unitaryPlanOthers
  ) {
    return <UnitaryHoverPopup />
  }
}

export default function LocationMap({
  defaultTabName = '',
  locationBarOptionValue = '',
  type = '',
  defaultValue = {},
  houseId, // 如果没有houseId, 则不显示周边设施
  propertyId,
  fitPadding,
  children,
  house
}) {
  const { longitude, latitude } = defaultValue
  const { isMobile } = useApp()
  
  const { pushTrack } = useTracking()
  const [fired, setFired] = useState(false)

  // 是否隐藏地址
  const hideAddress = house.publishAddress === false

  const handleClick = useCallback(() => {
    if (!fired && isAU) {
      pushTrack('homely_map_interaction', houseToGta(house))
      setFired(true)
    }
  }, [fired, house, pushTrack])

  const opt = useMemo(() => {
    const tmp = {}
    
    if(!hideAddress && house.lng && house.lat) {
      tmp.traffic = { locale: [house.lng, house.lat] }
    }
    if(houseId) {
      tmp.facility = { houseId }
    }

    return tmp
  }, [hideAddress, house.lat, house.lng, houseId])
  
  return (
    <Mapbox initialViewState={defaultValue}>
      <OverallLayer type={type} {...opt} onClick={handleClick} />
      {/* 卫星图、放大缩小、全屏控制面板 */}
      <div className="absolute z-10 right-4 top-28 md:top-1/2 md:-translate-y-1/2">
        <div className="flex flex-col gap-2">
          {isMobile && <LayersController type={type} {...opt} />}
          <NavigationController small vertical />
          <FullscreenController small />
        </div>
      </div>
      {/* Marker */}
      {!hideAddress && (
        <Locale
          latitude={latitude}
          longitude={longitude}
          style={{ zIndex: 5 }}
        >
          <div className="text-error">
            <SVGLocation width="1.8rem" height="1.8rem" />
          </div>
        </Locale>
      )}
      <Marker disabledMarker={defaultValue} researchAlwaysOnMove />
      <Layer />
      <Popup />
      <MapboxInitial 
        type={type}
        locationBarOptionValue={locationBarOptionValue} 
        defaultTabName={defaultTabName}
      />
      {!hideAddress && (
        <LocationBoundary 
          propertyId={propertyId} 
          fitPadding={fitPadding}
          boundaryLineColor="red"
        />
      )}
      <UnitaryPopup />
      {children}
    </Mapbox>
  )
}

LocationMap.propTypes = {
  defaultTabName: PropTypes.string,
  type: PropTypes.string,
  defaultValue: PropTypes.object,
  houseId: PropTypes.number,
  fitPadding: PropTypes.number,
  children: PropTypes.node,
}
